/**
 * Native-fetch helpers for the REST endpoints used by the SetupPDF view.
 *
 * None of these endpoints are Hasura GraphQL calls — all are plain JSON REST
 * calls to the stories service. `fetchStoryPdf` is the single-story preview
 * branch (inlined from the retired `storyViewEffects.previewPdf`).
 */

// `useMutation` used directly (not useDynamicFetchMutation from bnp-core) because
// the stories service uses cookie-based session auth and requires `credentials: 'same-origin'`.
// bnp-core's fetch helpers use Auth0 Bearer tokens and do not include that header,
// so requests would be rejected as 401. `postJson` in fetchUtils.ts sets
// `credentials: 'same-origin'` explicitly.
import { useMutation } from '@tanstack/react-query';
import { IStoryMargins } from '@opengov/stories-shared/types';
import { getJson, postJson } from '../../../../utils/fetchUtils';
import { useToast } from '../../../../components/Toast/context/useToast.context';

// ---- TOC PDF preview ----

export type TocPdfParams = {
  html: string;
  publicationId: string;
  entityId: number;
  displayTitle: boolean;
  displayDate: boolean;
  displayPageNumber: boolean;
  enlargeFonts: boolean;
  startingPage: string;
  storyMarginsEnabled: boolean;
  margins: IStoryMargins;
  showHeader: boolean;
  showFooter: boolean;
  watermark: string | null;
  controller: AbortController;
};

/**
 * Fetch a TOC PDF preview. Throws on network error, non-ok response, or when
 * the server returns no PDF. Throws `AbortError` when the controller is
 * aborted — callers should swallow that error silently.
 */
export async function fetchTocPdf(params: TocPdfParams): Promise<string> {
  const { controller, ...rest } = params;
  const data = await postJson<{ pdf?: string }>(
    '/api/stories/v1/stories/toc-pdf',
    {
      html: rest.html,
      publicationId: rest.publicationId,
      entityId: rest.entityId,
      displayTitle: rest.displayTitle,
      displayDate: rest.displayDate,
      displayPageNumber: rest.displayPageNumber,
      enlargeFonts: rest.enlargeFonts,
      startingPage: rest.startingPage,
      storyMarginsEnabled: rest.storyMarginsEnabled,
      margins: rest.margins,
      template: false,
      showHeader: rest.showHeader,
      showFooter: rest.showFooter,
      watermark: rest.watermark
    },
    controller.signal
  );
  if (!data.pdf) {
    throw new Error('No PDF in TOC response');
  }
  return data.pdf;
}

// ---- Single-story PDF preview ----

export type StoryPdfParams = {
  storyId: string;
  isTemplate: boolean;
  displayDate: boolean;
  displayPageNumber: boolean;
  displayTitle: boolean;
  enlargeFonts: boolean;
  startingPage: string;
  storyMarginsEnabled: boolean;
  margins: IStoryMargins;
  showHeader: boolean;
  showFooter: boolean;
  isExportFromSetupPdf: boolean;
  displayDummyPageNumber: boolean;
  watermark: string | null;
  controller: AbortController;
};

/**
 * Fetch a single-story PDF preview via GET (inlined from
 * `storyViewEffects.previewPdf`). Returns `''` when the server returns no PDF.
 * Throws `AbortError` when the controller is aborted — callers should swallow
 * that error silently.
 *
 * `showMargins` is inverted from `storyMarginsEnabled`: it tells the content
 * export service to include Puppeteer margins, which we turn off when
 * `product-stories-feature-story-margins` is enabled (margins handled elsewhere).
 * `margins` is passed as a JSON string; `URLSearchParams` encodes it once.
 */
export async function fetchStoryPdf(params: StoryPdfParams): Promise<string> {
  const {
    storyId,
    isTemplate,
    displayDate,
    displayPageNumber,
    displayTitle,
    enlargeFonts,
    startingPage,
    storyMarginsEnabled,
    margins,
    showHeader,
    showFooter,
    isExportFromSetupPdf,
    displayDummyPageNumber,
    watermark,
    controller
  } = params;

  const query = new URLSearchParams({
    displayPageNumber: String(displayPageNumber),
    displayTitle: String(displayTitle),
    displayDate: String(displayDate),
    enlargeFonts: String(enlargeFonts),
    template: String(isTemplate),
    showMargins: String(!storyMarginsEnabled),
    startingPage,
    margins: JSON.stringify(margins),
    showHeader: String(showHeader),
    showFooter: String(showFooter),
    isExportFromSetupPdf: String(isExportFromSetupPdf),
    watermark: watermark ?? '',
    displayDummyPageNumber: String(displayDummyPageNumber)
  });

  const data = await getJson<{ pdf?: string }>(
    `/api/stories/v1/stories/${storyId}/pdf?${query.toString()}`,
    controller.signal
  );
  return data?.pdf ?? '';
}

// ---- Export publication PDF ----

type ExportPdfPayload = {
  publicationId?: string | null;
  sectionId?: string | null;
  pdfOptions: Record<string, unknown>;
};

export function useExportPublicationPdf(entityId: number, errorTitle: string) {
  const { toggleToast } = useToast();
  const mutation = useMutation<unknown, Error, ExportPdfPayload>({
    mutationKey: ['exportPublicationPdf'],
    mutationFn: ({ publicationId, sectionId, pdfOptions }: ExportPdfPayload) => {
      const url = publicationId
        ? `/api/stories/v1/publication/${publicationId}/export-pdf`
        : `/api/stories/v1/folders/${sectionId}/export-pdf`;
      return postJson(url, { pdf_options: pdfOptions, entity_id: entityId });
    },
    onError: () => {
      toggleToast({ title: errorTitle, severity: 'error' });
    }
  });

  return mutation;
}
